type MongoDbLike = {
  databaseName?: string
  command: (command: Record<string, unknown>) => Promise<unknown>
}

export type SearchTextStageOptions = {
  index: string
  query: string
  path: string | string[]
  highlightPath?: string | string[]
}

export type SearchTextStage = {
  $search: {
    index: string
    text: {
      query: string
      path: string | string[]
    }
    highlight?: {
      path: string | string[]
    }
  }
}

export const buildSearchTextStage = (options: SearchTextStageOptions): SearchTextStage => {
  const index = options.index.trim()
  if (!index) throw new Error("Missing search index name")

  const query = options.query.trim()
  if (!query) throw new Error("Missing search query")

  const stage: SearchTextStage = {
    $search: {
      index,
      text: {
        query,
        path: options.path,
      },
    },
  }

  if (options.highlightPath) {
    stage.$search.highlight = { path: options.highlightPath }
  }

  return stage
}

export const searchMetaProjection = () => {
  return {
    score: { $meta: "searchScore" },
    highlights: { $meta: "searchHighlights" },
  } as const
}

export type EnsureSearchIndexParams = {
  db: MongoDbLike
  collection: string
  name: string
  definition: Record<string, unknown>
}

const listResultHasIndex = (listResult: unknown, name: string): boolean => {
  if (!listResult || typeof listResult !== "object") return false
  if (!("cursor" in listResult)) return false
  const cursor = (listResult as { cursor?: unknown }).cursor
  if (!cursor || typeof cursor !== "object") return false
  const firstBatch = (cursor as { firstBatch?: unknown }).firstBatch
  if (!Array.isArray(firstBatch)) return false
  return firstBatch.some((idx) => idx && typeof idx === "object" && "name" in idx && (idx as { name?: unknown }).name === name)
}

const isIndexAlreadyExistsError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false
  const codeName = "codeName" in error ? (error as { codeName?: unknown }).codeName : undefined
  if (codeName === "IndexAlreadyExists") return true
  const message = "message" in error ? String((error as { message?: unknown }).message ?? "") : ""
  return /already exists/i.test(message)
}

export const ensureSearchIndex = async (params: EnsureSearchIndexParams): Promise<{ created: boolean }> => {
  const collection = params.collection.trim()
  if (!collection) throw new Error("Missing collection name")

  const name = params.name.trim()
  if (!name) throw new Error("Missing search index name")

  let listResult: unknown
  try {
    listResult = await params.db.command({ listSearchIndexes: collection })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`listSearchIndexes failed for "${collection}": ${message}`)
  }

  if (listResultHasIndex(listResult, name)) {
    return { created: false }
  }

  try {
    await params.db.command({
      createSearchIndexes: collection,
      indexes: [{ name, definition: params.definition }],
    })
  } catch (error) {
    if (isIndexAlreadyExistsError(error)) {
      return { created: false }
    }
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`createSearchIndexes failed for "${collection}" (index "${name}"): ${message}`)
  }

  return { created: true }
}
